import { alertDialog } from '@/components/AppDialogs';
import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import { useTheme, alpha } from '@mui/material/styles';
import { PushPin as PinIcon } from '@mui/icons-material';
import { createPortal } from 'react-dom';
import { lazyRetry } from '@utils/lazyRetry';
import NewWindow from 'react-new-window';

// Viewer shells pull in heavy dependencies (e.g. @mui/x-data-grid, PDF tooling).
// They only render when a viewer is actually opened, so load them lazily to keep
// them out of the app's entry chunk (which loads on every page).
const DocumentViewerShell = lazyRetry(() =>
  import('../components/DocumentViewerShell').then((m) => ({ default: m.DocumentViewerShell }))
);
const IndexingViewerShell = lazyRetry(() => import('../components/IndexingViewerShell'));
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { CircularProgress } from '@mui/material';
import { popoutStyles } from './popoutStyles';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Types of viewers we currently support.
export type ViewerType = 'document' | 'indexing' | null;

interface ViewerState {
  type: ViewerType;
  // Extra props can be passed to the specific viewer implementation later.
  props?: any;
}

interface PinnedItem {
  id: string;
  docId: string;
  docOrder: number;
  page: number;
  docIndex: number;
  timestamp: number;
  batchId?: string;
}

// Custom hook to manage pinned items globally with chat-specific isolation
export function usePersistentPinnedItems() {
  const STORAGE_KEY = 'documentViewer_pinnedItems';
  const CHAT_ID_KEY = 'documentViewer_currentChatId';
  
  // Track current chat ID
  const [currentChatId, setCurrentChatId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(CHAT_ID_KEY);
    } catch {
      return null;
    }
  });
  
  const [pinnedItems, setPinnedItemsState] = useState<PinnedItem[]>(() => {
    // Initialize from localStorage on first load
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedChatId = localStorage.getItem(CHAT_ID_KEY);
      
      // If no stored chat ID or it's a different chat, start fresh
      if (!storedChatId) {
        return [];
      }
      
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load pinned items from localStorage:', error);
      return [];
    }
  });

  // Save to localStorage whenever pinnedItems changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pinnedItems));
    } catch (error) {
      console.warn('Failed to save pinned items to localStorage:', error);
    }
  }, [pinnedItems]);

  // Save chat ID whenever it changes
  useEffect(() => {
    try {
      if (currentChatId) {
        localStorage.setItem(CHAT_ID_KEY, currentChatId);
      } else {
        localStorage.removeItem(CHAT_ID_KEY);
      }
    } catch (error) {
      console.warn('Failed to save chat ID to localStorage:', error);
    }
  }, [currentChatId]);

  const setPinnedItems = (updater: React.SetStateAction<PinnedItem[]>) => {
    setPinnedItemsState(updater);
  };

  const clearPinnedItems = () => {
    setPinnedItemsState([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear pinned items from localStorage:', error);
    }
  };

  // Function to start a new chat (clears all pinned items and resets document viewer)
  const startNewChat = (newChatId?: string) => {
    const chatId = newChatId || `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Clear all pinned items for the new chat
    clearPinnedItems();
    setCurrentChatId(chatId);
    
    // Note: DocumentViewerShell will automatically detect the chat change and clear its state
  };

  // Auto-detect new chat by checking for URL changes or other indicators
  useEffect(() => {
    // Extract current chat ID from URL path like /connect/{chatId}
    const detectChatIdFromUrl = () => {
      const path = window.location.pathname;
      const chatMatch = path.match(/\/(?:connect|capture)\/([^\/]+)/);
      return chatMatch ? chatMatch[1] : null;
    };

    const urlChatId = detectChatIdFromUrl();
    
    // If we detected a different chat ID from the URL, start a new chat session
    if (urlChatId && urlChatId !== currentChatId) {
      startNewChat(urlChatId);
      return;
    }

    // Generate initial chat ID if none exists and no URL chat ID detected
    if (!currentChatId && !urlChatId) {
      const newId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      startNewChat(newId);
      return;
    }

    // Listen for navigation events to detect chat switches
    const handleLocationChange = () => {
      const newUrlChatId = detectChatIdFromUrl();
      if (newUrlChatId && newUrlChatId !== currentChatId) {
        startNewChat(newUrlChatId);
      }
    };

    // Listen for popstate events (browser navigation)
    window.addEventListener('popstate', handleLocationChange);
    
    // Also listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };
    
    // Also expose globally for manual triggering
    (window as any).startNewChat = startNewChat;
    (window as any).clearPinnedDocuments = clearPinnedItems;
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [currentChatId]);

  return { 
    pinnedItems, 
    setPinnedItems, 
    clearPinnedItems, 
    startNewChat,
    currentChatId 
  };
}

interface ViewerContextValue {
  viewer: ViewerState;
  openDocument: (props?: any) => void;
  openIndexing: (props?: any) => void;
  closeViewer: () => void;
  // Add pinned items to context
  pinnedItems: PinnedItem[];
  setPinnedItems: (updater: React.SetStateAction<PinnedItem[]>) => void;
  clearPinnedItems: () => void;
  startNewChat: (newChatId?: string) => void;
  currentChatId: string | null;
}

const ViewerContext = createContext<ViewerContextValue | undefined>(undefined);

// Component to render pinned items in chat
function PinnedItemsDisplay({ currentBatchId }: { currentBatchId?: string }) {
  const theme = useTheme();
  const { pinnedItems, setPinnedItems, clearPinnedItems, startNewChat, currentChatId } = useViewer();
  
  // Store the last active batch ID so pinned items persist when viewer closes
  const [lastActiveBatchId, setLastActiveBatchId] = useState<string | undefined>(undefined);
  
  // Update last active batch when viewer is open
  useEffect(() => {
    if (currentBatchId) {
      setLastActiveBatchId(currentBatchId);
    }
  }, [currentBatchId]);
  
  // Use current batch if viewer is open, otherwise use last active batch
  const displayBatchId = currentBatchId || lastActiveBatchId;
  
  // Filter pinned items to show those from the relevant batch
  const currentBatchPinnedItems = displayBatchId 
    ? pinnedItems.filter(item => item.batchId === displayBatchId)
    : pinnedItems; // Show all pinned items if no batch context

  const handleUnpin = (pinnedId: string) => {
    setPinnedItems(prev => prev.filter(item => item.id !== pinnedId));
  };

  // Auto-scroll to bottom when pinned items change
  useEffect(() => {
    if (currentBatchPinnedItems.length > 0) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        const messagesEnd = document.querySelector('[data-scroll-target="messages-end"]');
        if (messagesEnd) {
          messagesEnd.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [currentBatchPinnedItems.length]);

  if (currentBatchPinnedItems.length === 0) return null;

  return createPortal(
    <Box sx={{
      display: 'flex',
      justifyContent: 'start', // Align like agent message
      mb: 2.5,
      maxWidth: '75%'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        width: '100%'
      }}>
        {/* Pinned Items Message Card */}
        <Card
          elevation={0}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 4
          }}
        >
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.05)
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PinIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                Pinned Document Pages
                {!currentBatchId && lastActiveBatchId && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                    (from previous session)
                  </Typography>
                )}
              </Typography>
              <Chip 
                size="small" 
                label={currentBatchPinnedItems.length} 
                color="primary" 
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={clearPinnedItems}
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': { 
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1)
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Pinned Items Grid */}
          <Box sx={{
            p: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1
          }}>
            {currentBatchPinnedItems.map((item) => (
              <Chip
                key={item.id}
                icon={<PinIcon sx={{ fontSize: 14 }} />}
                label={`Doc ${item.docOrder} Page ${item.page}`}
                variant="outlined"
                color="primary"
                size="small"
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                onDelete={() => handleUnpin(item.id)}
                sx={{
                  height: 32,
                  borderRadius: 3,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: theme.shadows[1],
                  '& .MuiChip-icon': {
                    fontSize: 14,
                    marginLeft: 1
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: 14,
                    marginRight: 1,
                    '&:hover': {
                      color: theme.palette.error.main
                    }
                  },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[3]
                  }
                }}
              />
            ))}
          </Box>

          {/* Footer with context info */}
          <Box sx={{
            px: 2,
            py: 1,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.default, 0.3)
          }}>
            <Typography variant="caption" color="text.secondary">
              These pages are available for reference in this chat session only.
              {!currentBatchId && lastActiveBatchId && (
                <Typography component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Reopen the document viewer to add more pinned pages.
                </Typography>
              )}
              <Typography component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem', opacity: 0.6 }}>
                Chat ID: {currentChatId?.slice(-8) || 'Unknown'} • Pinned items will be cleared when starting a new chat.
              </Typography>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>,
    // Insert into chat messages area, right before the scroll target (messages end)
    (() => {
      const messagesEnd = document.querySelector('[data-scroll-target="messages-end"]');
      if (messagesEnd && messagesEnd.parentElement) {
        return messagesEnd.parentElement;
      }
      // Fallback: look for the messages container
      const chatContainer = document.querySelector('.max-w-4xl.space-y-20');
      return chatContainer || document.body;
    })()
  );
}

// Component to detect chat changes from React Router params
// Use this component in your chat pages to automatically clear pinned items on chat change
export function ChatChangeDetector() {
  const { chatId } = useParams<{ chatId: string }>();
  const { startNewChat, currentChatId, closeViewer } = useViewer();

  useEffect(() => {
    if (chatId && chatId !== currentChatId) {
      closeViewer(); // Ensure any open viewer windows are closed
      startNewChat(chatId);
    }
  }, [chatId, currentChatId, startNewChat, closeViewer]);

  return null; // This component doesn't render anything
}

export const ViewerProvider = ({ children }: { children: ReactNode }) => {
  const [viewer, setViewer] = useState<ViewerState>({ type: null });
  const [blocked, setBlocked] = useState(false);
  const { pinnedItems, setPinnedItems, clearPinnedItems, startNewChat, currentChatId } = usePersistentPinnedItems();
  
  const openDocument = (props?: any) => setViewer({ type: 'document', props });
  const openIndexing = (props?: any) => setViewer({ type: 'indexing', props });
  const closeViewer = () => {
    setViewer({ type: null });
  };

  // Close viewer on browser navigation events (route changes)
  useEffect(() => {
    const handleRouteChange = () => {
      if (viewer.type) {
        closeViewer();
      }
    };
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('hashchange', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, [viewer.type]);

  React.useEffect(() => {
    (window as any).openDocumentViewer = openDocument;
    (window as any).openIndexingViewer = openIndexing;
    (window as any).closeViewer = closeViewer;
    
    // Helper function to open indexing viewer with current batch from document viewer
    (window as any).openIndexingViewerForCurrentBatch = () => {
      const currentBatchId = viewer.type === 'document' ? viewer.props?.batchId : undefined;
      if (currentBatchId) {
        openIndexing({ batchId: currentBatchId });
      } else {
        void alertDialog('No batch is currently loaded. Open a document viewer before opening the indexing viewer.', {"title":"Open a document first"});
      }
    };

    // Helper to open indexing viewer with a specific batch ID
    (window as any).openIndexingViewerWithBatch = (batchId: string) => {
      openIndexing({ batchId });
    };
  }, [viewer.type, viewer.props]);

  // Get current batch ID from viewer props
  const currentBatchId = viewer.type === 'document' ? viewer.props?.batchId : undefined;

  return (
    <ViewerContext.Provider value={{ 
      viewer, 
      openDocument, 
      openIndexing, 
      closeViewer,
      pinnedItems,
      setPinnedItems,
      clearPinnedItems,
      startNewChat,
      currentChatId
    }}>
      {children}
      
      {/* Persistent Pinned Items Display - Always render even when viewer is closed */}
      <PinnedItemsDisplay currentBatchId={currentBatchId} />
      
      {/* Primary pop-out viewer */}
      {!blocked && <ViewerPopout
        viewer={viewer}
        onClose={closeViewer}
        onBlock={() => setBlocked(true)}
      />}

      {/* Fallback drawer if popup blocked */}
      {blocked && <ViewerDrawer viewer={viewer} onClose={closeViewer} />}
    </ViewerContext.Provider>
  );
};

export const useViewer = () => {
  const context = useContext(ViewerContext);
  if (!context) throw new Error('useViewer must be used within a ViewerProvider');
  return context;
};

/* -------------------------------------------------------------------------- */
/*                            Pop-out implementation                          */
/* -------------------------------------------------------------------------- */

function ViewerPopout({
  viewer,
  onClose,
  onBlock
}: {
  viewer: ViewerState;
  onClose: () => void;
  onBlock: () => void;
}) {
  const { type, props } = viewer;
  const windowRef = useRef<Window | null>(null);
  const [cache, setCache] = useState<any>(null);
  const [stylesReady, setStylesReady] = useState(false);
  const [stylesCopied, setStylesCopied] = useState(false);

  // Handle window opening
  const handleWindowOpen = useCallback((win: Window) => {
    if (!win) {
      return;
    }

    try {
      const doc = win.document;
      
      // Don't use document.write - just ensure basic structure exists
      if (!doc.getElementById('app')) {
        const appDiv = doc.createElement('div');
        appDiv.id = 'app';
        appDiv.style.width = '100%';
        appDiv.style.height = '100vh';
        appDiv.style.margin = '0';
        appDiv.style.padding = '0';
        doc.body.appendChild(appDiv);
      }

      // Add basic styles directly to head
      const style = doc.createElement('style');
      style.textContent = `
        html, body { margin: 0; padding: 0; height: 100%; font-family: system-ui; }
        * { box-sizing: border-box; }
        #app { width: 100%; height: 100vh; }
        ${popoutStyles}
      `;
      doc.head.appendChild(style);

      // Add Material-UI fonts
      const robotoFont = doc.createElement('link');
      robotoFont.rel = 'stylesheet';
      robotoFont.href = 'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap';
      doc.head.appendChild(robotoFont);

      const materialIcons = doc.createElement('link');
      materialIcons.rel = 'stylesheet';
      materialIcons.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      doc.head.appendChild(materialIcons);

      // Copy authentication from parent
      const parentJwtToken = window.localStorage.getItem('jwt_token');
      if (parentJwtToken) {
        win.localStorage.setItem('jwt_token', parentJwtToken);
      }

      // Create Emotion cache
      const newCache = createCache({ 
        key: 'mui-pop',
        prepend: true,
        container: doc.head 
      });

      // Set initial state - allow immediate rendering
      windowRef.current = win;
      setCache(newCache);
      setStylesReady(true);

      // Copy styles after a delay to improve styling
      setTimeout(() => {
        // Copy existing stylesheets from parent window
        const parentStylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
        parentStylesheets.forEach((link) => {
          const clone = link.cloneNode(true) as HTMLLinkElement;
          doc.head.appendChild(clone);
        });

        // Copy existing style tags from parent window
        const parentStyles = Array.from(document.querySelectorAll('style'));
        parentStyles.forEach((style) => {
          const clone = style.cloneNode(true) as HTMLStyleElement;
          doc.head.appendChild(clone);
        });

        setStylesCopied(true);
      }, 750); // Increased delay for slower connections

      // Clean up on window close
      const handleUnload = () => {
        windowRef.current = null;
        setCache(null);
        setStylesReady(false);
        setStylesCopied(false);
      };
      win.addEventListener('beforeunload', handleUnload);

      return () => {
        win.removeEventListener('beforeunload', handleUnload);
      };
    } catch (error) {
      console.error('❌ Error during window setup:', error);
      // Try to render with minimal setup
      const doc = win.document;
      const fallbackCache = createCache({ 
        key: 'mui-pop-fallback', 
        container: doc.head 
      });
      windowRef.current = win;
      setCache(fallbackCache);
      setStylesReady(true);
      setStylesCopied(true);
    }
  }, [type]);

  // Reset state when viewer type changes to null (closing)
  useEffect(() => {
    if (type === null) {
      windowRef.current = null;
      setCache(null);
      setStylesReady(false);
      setStylesCopied(false);
    }
  }, [type]);

  if (!type) return null;

  const title = type === 'document' ? 'Document Viewer' : 'Indexing Viewer';

  return (
    <NewWindow
      title={title}
      center="screen"
      features={{ width: 1600, height: 500, resizable: 'yes', scrollbars: 'yes' }}
      onUnload={onClose}
      onBlock={onBlock}
      onOpen={handleWindowOpen}
    >
      {cache && stylesReady ? (
        <>
          <CacheProvider value={cache}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={createTheme({ palette: { mode: 'light' } })}>
                <CssBaseline />
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: '100vh',
                  backgroundColor: '#fff',
                  opacity: stylesCopied ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
                }}>
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    minHeight: '500px',
                    position: 'relative'
                  }}>
                    <Suspense
                      fallback={
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <CircularProgress />
                        </Box>
                      }
                    >
                      {type === 'document' && <DocumentViewerShell {...props} />}
                      {type === 'indexing' && <IndexingViewerShell {...props} />}
                    </Suspense>
                  </Box>
                </Box>
              </ThemeProvider>
            </StyledEngineProvider>
          </CacheProvider>
          
          {/* Loading overlay - only show while styles are loading */}
          {!stylesCopied && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#fff',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontSize: '14px',
              color: '#666',
              zIndex: 9999
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid rgba(0,0,0,0.1)',
                borderTopColor: '#666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
              }} />
              <div>Loading document viewer...</div>
              <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>
                Loading styles...
              </div>
              <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
              `}</style>
            </div>
          )}
        </>
      ) : (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '14px',
          color: '#666'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(0,0,0,0.1)',
            borderTopColor: '#666',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }} />
          <div>Loading document viewer...</div>
          <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>
            {stylesReady && !stylesCopied ? 'Loading styles...' : 'Preparing viewer...'}
          </div>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}
    </NewWindow>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Drawer fallback                              */
/* -------------------------------------------------------------------------- */

function ViewerDrawer({ viewer, onClose }: { viewer: ViewerState; onClose: () => void }) {
  const { type, props } = viewer;

  const renderContent = (title: string) => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 1,
          px: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          }
        >
          {title.startsWith('Document') && <DocumentViewerShell {...props} />}
          {title.startsWith('Indexing') && <IndexingViewerShell {...props} />}
        </Suspense>
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={type === 'document'}
        onClose={onClose}
        PaperProps={{ sx: { width: '60vw', maxWidth: 900 } }}
      >
        {renderContent('Document Viewer')}
      </Drawer>

      <Drawer
        anchor="bottom"
        open={type === 'indexing'}
        onClose={onClose}
        PaperProps={{ sx: { height: '60vh' } }}
      >
        {renderContent('Indexing Viewer')}
      </Drawer>
    </>
  );
} 